export type LookupEnergy = "electricity" | "gas";
export type LookupInput =
	| { kind: "address"; address: string; addressKey: string; energy: LookupEnergy; promo: string }
	| { kind: "nmi"; nmi: string; energy: "electricity"; promo: string };
export type SubmittedLookup = LookupInput & { revision: number };
export type PlanRequest = { energy: LookupEnergy; promo?: string } & (
	| { addressKey: string; nmi?: never; dpid?: never }
	| { nmi: string; addressKey?: never; dpid?: never }
	| { dpid: string; addressKey?: never; nmi?: never }
);

export const propertyParameters = [
	"address",
	"addressKey",
	"nmi",
	"addressDetail",
	"globalAddressKey",
	"dpid",
];
export const restartLookupMessage =
	"Please enter your address or NMI to view the available product.";
export const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);
export const isNonEmptyString = (value: unknown): value is string =>
	typeof value === "string" && value.trim().length > 0;

export function isSubmittedLookup(value: unknown): value is SubmittedLookup {
	if (
		!isRecord(value) ||
		!Number.isSafeInteger(value.revision) ||
		Number(value.revision) < 1 ||
		typeof value.promo !== "string"
	)
		return false;
	if (value.kind === "nmi") return value.energy === "electricity" && isNonEmptyString(value.nmi);
	return (
		value.kind === "address" &&
		(value.energy === "electricity" || value.energy === "gas") &&
		isNonEmptyString(value.address) &&
		isNonEmptyString(value.addressKey)
	);
}

export function applyOnlineUrl(energy: LookupEnergy, promo = "") {
	const params = new URLSearchParams({ energy });
	if (promo) params.set("promo", promo);
	return `/apply-online?${params.toString()}`;
}

export function cleanLegacyLookupUrl(input: URL): URL | null {
	const url = new URL(input);
	const restart = propertyParameters.some(key => url.searchParams.has(key));
	if (!restart && !url.searchParams.has("requestID")) return null;
	for (const key of [...propertyParameters, "requestID"]) url.searchParams.delete(key);
	if (restart) url.searchParams.set("restart", "1");
	return url;
}

export function lookupMatchesPublicParams(
	lookup: SubmittedLookup,
	energy?: string,
	promo?: string,
) {
	return (!energy || lookup.energy === energy) && lookup.promo === (promo || "");
}

export function parsePlanRequest(body: unknown): PlanRequest | null {
	if (!isRecord(body)) return null;
	const energy = body.energy ?? "electricity";
	if (energy !== "electricity" && energy !== "gas") return null;
	if (body.promo !== undefined && typeof body.promo !== "string") return null;
	const keys = ["addressKey", "nmi", "dpid"] as const;
	const present = keys.filter(key => body[key] !== undefined);
	if (present.length !== 1 || !isNonEmptyString(body[present[0]])) return null;
	const key = present[0];
	if (energy === "gas" && key !== "addressKey") return null;
	return { energy, promo: body.promo ?? "", [key]: body[key] } as PlanRequest;
}

export async function readPlanResponse(response: Response) {
	const data = await response.json();
	// Discovery returns 422 when the customer needs to choose between NMIs.
	const needsNmiSelection =
		response.status === 422 &&
		data?.success === true &&
		data?.status === "multipleNMI" &&
		Array.isArray(data.addresses);
	if (!response.ok && !needsNmiSelection) throw new Error("Failed to get plans");
	return data;
}
